import axios from 'axios';

const MIN_SEGMENT_LENGTH = 3;

/**
 * Look up users through Zero. `nameSegment` accepts either a name fragment or a
 * full email address -- an email triggers an exact match, which is how the
 * signed-in sender is hydrated.
 *
 * Zero requires a bearer token and the `x-zero-client` header; cookies are not
 * accepted, so credentials are deliberately left off the request.
 *
 * @param {string} nameSegment name fragment or full email, min 3 characters
 * @returns {Promise<Array<Object>>} matching users, empty when none resolve
 */
export async function searchUsers(nameSegment) {
  const segment = (nameSegment || '').trim();
  if (segment.length < MIN_SEGMENT_LENGTH) {
    return [];
  }

  const res = await axios.post(
    process.env.REACT_APP_AD_SEARCH_API,
    { nameSegment: segment },
    {
      headers: {
        Authorization: `Bearer ${window.sessionStorage.accessToken}`,
        'Content-Type': 'application/json',
        'x-zero-client': 'app'
      }
    }
  );

  return Array.isArray(res.data.users) ? res.data.users : [];
}

/**
 * Resolve a single user by exact email. Zero is a search endpoint, so the
 * address is re-checked against the results before the record is trusted.
 *
 * Never rejects: enrichment is best-effort and must not block a submission.
 *
 * @param {string} email
 * @returns {Promise<Object|null>} the matching user, or null when unresolved
 */
export async function lookupUserByEmail(email) {
  const wanted = (email || '').trim().toLowerCase();
  if (!wanted) {
    return null;
  }

  try {
    const users = await searchUsers(wanted);
    return users.find(user => (user.email || '').toLowerCase() === wanted) || null;
  } catch (err) {
    logger.log('User lookup failed for enrichment :: ', err);
    return null;
  }
}

/**
 * Map a Zero user record onto the optional submission fields for one role.
 * Keys the directory did not return are omitted entirely rather than sent as
 * empty strings, so the API falls back to its own lookup instead of storing a
 * blank value.
 *
 * @param {Object|null} user a record from `searchUsers`
 * @param {'sender'|'recipient'} role which side of the submission to build for
 * @returns {Object} submission fields, possibly empty
 */
export function toSubmissionFields(user, role) {
  if (!user) {
    return {};
  }

  const fields = {};
  if (user.managerEmail) fields[`${role}ManagerEmail`] = user.managerEmail;
  if (user.org) fields[`${role}Org`] = user.org;
  if (user.title) fields[`${role}Title`] = user.title;

  return fields;
}

/**
 * Nike-owned addresses map to 'nike'; Converse addresses map to 'converse'.
 *
 * Lowercase on purpose: the submission stream passes this value straight into
 * `maximBehaviors(company, maxim)`, which matches 'nike'/'converse' exactly. A
 * capitalized value silently resolves to no behaviors and the recipient,
 * sender, and manager emails all render without them.
 *
 * @param {string} email
 * @returns {string} 'converse' or 'nike'
 */
export function companyFromEmail(email) {
  return (email || '').toLowerCase().endsWith('@converse.com') ? 'converse' : 'nike';
}



// WEBPACK FOOTER //
// ./src/services/userLookup.js